import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  MessageSquare, SkipForward, Star, Flame, Calendar,
  TrendingUp, ChevronDown, ChevronUp, Lock, Globe2, Info,
} from 'lucide-react';
import { SectionCard } from './ProfileWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  spark:        { bg: 'bg-amber-100',   text: 'text-amber-700',  dot: 'bg-amber-400' },
  depth:        { bg: 'bg-orange-100',  text: 'text-orange-700', dot: 'bg-orange-500' },
  breakthrough: { bg: 'bg-purple-100',  text: 'text-purple-700', dot: 'bg-purple-500' },
};
const LEVEL_PALETTE = { spark: '#f59e0b', depth: '#ff780a', breakthrough: '#7c3aed' };
const TYPE_PALETTE  = ['#ff780a', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

const ScoreDot = ({ score }) => {
  if (!score) return <span className="text-charcoal/25 text-xs">—</span>;
  const color = score >= 8 ? '#22c55e' : score >= 5 ? '#ff780a' : '#ef4444';
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color }}>
      <Star size={10} fill={color} />{score}/10
    </span>
  );
};

const StatCard = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="p-4 rounded-2xl bg-white/60 border border-charcoal/5 flex flex-col gap-1">
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon size={14} className="text-charcoal/40" />}
      <span className="text-charcoal/50 text-xs uppercase tracking-wider">{label}</span>
    </div>
    <span className="font-serif text-2xl text-charcoal" style={accent ? { color: accent } : {}}>
      {value ?? '—'}
    </span>
    {sub && <span className="text-xs text-charcoal/40">{sub}</span>}
  </div>
);

const MiniBar = ({ label, value, max, color }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="text-charcoal/50 w-24 truncate capitalize">{label}</span>
    <div className="flex-1 h-1.5 bg-charcoal/10 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="h-full rounded-full"
        style={{ backgroundColor: color || '#ff780a' }}
      />
    </div>
    <span className="text-charcoal/60 w-6 text-right">{value}</span>
  </div>
);

const VisibilityToggle = ({ value, onChange, saving }) => (
  <div
    className="inline-flex items-center gap-1 p-1 rounded-full bg-charcoal/5"
    data-testid="diary-visibility-toggle"
  >
    {[
      { v: 'private', icon: Lock,    label: 'Private' },
      { v: 'public',  icon: Globe2, label: 'Public'  },
    ].map(opt => {
      const active = value === opt.v;
      return (
        <button
          key={opt.v}
          type="button"
          disabled={saving || active}
          onClick={() => onChange(opt.v)}
          data-testid={`visibility-${opt.v}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition-colors ${
            active ? 'bg-white text-charcoal shadow-sm' : 'text-charcoal/60 hover:text-charcoal'
          }`}
        >
          <opt.icon size={11} /> {opt.label}
        </button>
      );
    })}
  </div>
);

const CultureDiarySection = ({
  profile,
  userId,
  isOwn,
  organisation,
  onProfileUpdate,        // (newProfile) => void  (called when own visibility changes)
}) => {
  const [diary, setDiary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    axios.get(`${API}/profile/${userId}/diary`)
      .then(r => setDiary(r.data))
      .catch(() => setDiary(null))
      .finally(() => setLoading(false));
  }, [userId]);

  const visibility = (profile?.culture_diary_visibility || 'private');

  const handleVisibility = async (newValue) => {
    if (!isOwn || newValue === visibility) return;
    setSavingVisibility(true);
    try {
      const r = await axios.put(`${API}/profile/me`, { culture_diary_visibility: newValue });
      onProfileUpdate?.(r.data);
    } finally {
      setSavingVisibility(false);
    }
  };

  // Helper widgets render whether or not we have data
  const Explainer = () => (
    <div className="flex items-start gap-2 text-xs text-charcoal/60 bg-charcoal/[0.03] rounded-lg px-3 py-2">
      <Info size={13} className="text-charcoal/40 mt-0.5 flex-shrink-0" />
      <span>
        Your <strong>Culture Diary</strong> is a quiet log of every conversation you’ve had through this
        app — which thoughts you reflected on, the depth you went to, and how each one landed for you.
        It’s built automatically as you use the app; nothing extra to fill in.
      </span>
    </div>
  );

  // ── Read view: locked-out (teammate, owner set to private) ──────────────────
  if (!isOwn && diary?.private) {
    return (
      <SectionCard
        title="Culture Diary"
        subtitle={`${diary?.owner?.display_name || 'They'} keep this private.`}
        testId="section-culture-diary"
      >
        <Explainer />
        <div className="text-center py-8 text-charcoal/40 text-sm flex flex-col items-center gap-2">
          <Lock size={20} className="text-charcoal/30" />
          Hidden by the owner.
        </div>
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <SectionCard title="Culture Diary" subtitle="A log of every conversation you've had."
                   testId="section-culture-diary">
        <Explainer />
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin" />
        </div>
      </SectionCard>
    );
  }

  const stats = diary?.stats;
  const discussions = diary?.discussions || [];
  const visibleDiscussions = showAll ? discussions : discussions.slice(0, 12);

  const maxLevel = stats ? Math.max(...Object.values(stats.by_level || {}), 1) : 1;
  const maxType  = stats ? Math.max(...Object.values(stats.by_type  || {}), 1) : 1;
  const typeKeys = Object.entries(stats?.by_type || {}).sort((a, b) => b[1] - a[1]);
  const levelKeys = ['spark', 'depth', 'breakthrough'].filter(l => (stats?.by_level || {})[l]);

  const owner = diary?.owner || {};

  return (
    <SectionCard
      title="Culture Diary"
      subtitle="A quiet log of every conversation."
      testId="section-culture-diary"
    >
      <Explainer />

      {/* Visibility row */}
      {isOwn ? (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1">
          <span className="text-xs text-charcoal/50">
            Who can see this section?
          </span>
          <VisibilityToggle
            value={visibility}
            onChange={handleVisibility}
            saving={savingVisibility}
          />
        </div>
      ) : (
        <div className="text-xs text-charcoal/40 inline-flex items-center gap-1">
          <Globe2 size={11} /> Shared with {organisation || 'their team'}
        </div>
      )}

      {/* Top stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
        <StatCard label="Conversations" value={stats?.total_discussed ?? 0}
                  icon={MessageSquare} accent="#ff780a" />
        <StatCard label="Skipped"       value={stats?.total_skipped ?? 0}
                  icon={SkipForward} />
        <StatCard label="Days Active"   value={stats?.days_active ?? 0}
                  icon={Calendar} />
        <StatCard label="Avg Score"
                  value={stats?.avg_score != null ? `${stats.avg_score}/10` : '—'}
                  icon={Star}
                  accent={stats?.avg_score >= 7 ? '#22c55e' : '#ff780a'} />
      </div>

      {/* Level & points (own profile only — sourced from auth-me upstream) */}
      {isOwn && (owner.total_points > 0 || owner.streak_days > 0) && (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-orange-primary/5 border border-orange-primary/15">
          <div className="w-10 h-10 rounded-full bg-orange-primary/10 flex items-center justify-center flex-shrink-0">
            <Flame size={18} className="text-orange-primary" />
          </div>
          <div className="flex-1">
            <div className="text-charcoal/70 text-sm">
              {owner.total_points} points
              {owner.streak_days > 0 && (
                <span className="ml-2 text-orange-primary">{owner.streak_days} day streak</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* By Depth */}
      {levelKeys.length > 0 && (
        <div>
          <h3 className="text-charcoal/50 text-xs uppercase tracking-wider mb-3">By Depth</h3>
          <div className="space-y-2.5">
            {levelKeys.map(l => (
              <MiniBar key={l} label={l} value={stats.by_level[l]}
                       max={maxLevel} color={LEVEL_PALETTE[l]} />
            ))}
          </div>
        </div>
      )}

      {/* By Type */}
      {typeKeys.length > 0 && (
        <div>
          <h3 className="text-charcoal/50 text-xs uppercase tracking-wider mb-3">By Type</h3>
          <div className="space-y-2.5">
            {typeKeys.map(([t, v], i) => (
              <MiniBar key={t} label={t} value={v}
                       max={maxType} color={TYPE_PALETTE[i % TYPE_PALETTE.length]} />
            ))}
          </div>
        </div>
      )}

      {/* Discussions */}
      <div>
        <h3 className="text-charcoal/50 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
          <TrendingUp size={12} /> Conversations
        </h3>

        {discussions.length === 0 ? (
          <div className="text-center py-8 text-charcoal/40 text-sm">
            <MessageSquare size={28} className="mx-auto mb-2 opacity-30" />
            {isOwn
              ? 'No conversations yet — start with a thought on the home page.'
              : 'Nothing here yet.'}
          </div>
        ) : (
          <div className="space-y-2">
            {visibleDiscussions.map((d, i) => {
              const colors = LEVEL_COLORS[d.level] || LEVEL_COLORS.spark;
              const date = d.completed_at
                ? new Date(d.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                : '';
              const isExp = expanded === d.id;
              return (
                <motion.div
                  key={d.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="rounded-xl border border-charcoal/5 bg-white/50 overflow-hidden"
                  data-testid={`diary-entry-${d.id}`}
                >
                  <button
                    type="button"
                    className="w-full text-left p-3 flex items-start gap-3"
                    onClick={() => setExpanded(isExp ? null : d.id)}
                  >
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${colors.dot}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-charcoal text-sm leading-snug ${isExp ? '' : 'line-clamp-2'}`}>
                        {d.content}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                          {d.level}
                        </span>
                        <span className="text-xs text-charcoal/30 capitalize">{d.type}</span>
                        <span className="text-xs text-charcoal/30">{date}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <ScoreDot score={d.score} />
                      {isExp
                        ? <ChevronUp   size={14} className="text-charcoal/30" />
                        : <ChevronDown size={14} className="text-charcoal/30" />}
                    </div>
                  </button>
                </motion.div>
              );
            })}

            {discussions.length > 12 && (
              <button
                type="button"
                onClick={() => setShowAll(v => !v)}
                className="w-full py-2 text-sm text-charcoal/50 hover:text-charcoal transition-colors"
              >
                {showAll ? 'Show less' : `Show all ${discussions.length} conversations`}
              </button>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default CultureDiarySection;
